/**
 * CSS Loading Utility
 * Provides functions to load CSS files asynchronously to improve initial page load performance
 */

/**
 * Load a CSS file asynchronously
 * @param {string} href - The CSS file URL
 * @param {string} media - Media query (default: 'all')
 * @returns {Promise} - Promise that resolves when CSS is loaded
 */
export function loadCSS(href, media = 'all') {
  return new Promise((resolve, reject) => {
    // Check if CSS is already loaded
    const existingLink = document.querySelector(`link[href="${href}"]`);
    if (existingLink) {
      resolve(existingLink);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.media = media;
    
    // For non-critical CSS, use print media first, then switch to all
    if (media === 'print') {
      link.onload = function() {
        this.media = 'all';
        resolve(link);
      };
    } else {
      link.onload = () => resolve(link);
    }
    
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
    
    document.head.appendChild(link);
  });
}

/**
 * Load multiple CSS files asynchronously
 * @param {Array} cssFiles - Array of CSS file objects {href, media}
 * @returns {Promise} - Promise that resolves when all CSS files are loaded
 */
export function loadMultipleCSS(cssFiles) {
  const promises = cssFiles.map(css => loadCSS(css.href, css.media));
  return Promise.all(promises);
}

/**
 * Load CSS with print media trick for non-critical stylesheets
 * This prevents render-blocking while still loading the CSS
 * @param {string} href - The CSS file URL
 * @returns {Promise} - Promise that resolves when CSS is loaded
 */
export function loadNonCriticalCSS(href) {
  return loadCSS(href, 'print');
}

/**
 * Preload CSS file (hint to browser to fetch but don't block)
 * @param {string} href - The CSS file URL
 */
export function preloadCSS(href) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'style';
  link.href = href;
  link.onload = function() {
    this.rel = 'stylesheet';
  };
  document.head.appendChild(link);
}
